import { startCase } from 'es-toolkit/compat';
import type {
  ImprovementsProgressItemModel,
  MaintenanceMediaModel,
  MaintenanceOwnerResourceModel,
  SpaceListingModel,
} from 'api/models';
import { LABEL_COLOR_TYPES } from 'components/Label/Label';
import type { LabelColorType } from 'components/Label/labelTypes';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';

type Trade = NonNullable<ImprovementsProgressItemModel['trade']>;
type PreviousReportSubType = string | undefined;

const TRADE_LABEL_OVERRIDES: Partial<Record<Trade, string>> = {
  DoorsAndWindows: 'Doors & Windows',
  DrywallAndPlaster: 'Drywall & Plaster',
  GardeningAndLandscaping: 'Gardening & Landscaping',
  KeysAndAccess: 'Keys & Access',
  MasonryAndConcrete: 'Masonry & Concrete',
  RemediationAndRestoration: 'Remediation & Restoration',
  CabinetsCountertops: 'Cabinets & Countertops',
  AirConditioningHeating: 'Air Conditioning & Heating',
};

// `previousReportSubType` has seven possible values and only these two have
// homeowner-facing copy. Anything else returns '' rather than being
// `startCase`d into internal wording (e.g. 'Improvements QA Inspection').
const SOURCE_COPY: Record<string, { label: string; adjective: string }> = {
  ImprovementsRepairsInspection: {
    label: POST_INSPECTION_FLOW_STRINGS['progress.source.onboarding.label'],
    adjective:
      POST_INSPECTION_FLOW_STRINGS['progress.source.onboarding.adjective'],
  },
  ImprovementsMoveOutInspection: {
    label: POST_INSPECTION_FLOW_STRINGS['progress.source.move_out.label'],
    adjective:
      POST_INSPECTION_FLOW_STRINGS['progress.source.move_out.adjective'],
  },
};

export function getTradeLabel(trade: Trade | undefined): string {
  if (!trade) return '';
  return TRADE_LABEL_OVERRIDES[trade] ?? startCase(trade);
}

export function getSourceLabel(subType: PreviousReportSubType): string {
  if (!subType) return '';
  return SOURCE_COPY[subType]?.label ?? '';
}

export function getSourceAdjective(subType: PreviousReportSubType): string {
  if (!subType) return '';
  return SOURCE_COPY[subType]?.adjective ?? '';
}

/**
 * Rooms only. A Feature owner names a thing, not a place, so it is skipped
 * rather than shown - and the 'Around The Home' fallback other surfaces use
 * belongs to a different context and would mislead here.
 */
export function getRoomLabels(
  owners: MaintenanceOwnerResourceModel[] | undefined,
  spaces: SpaceListingModel[] | undefined,
): string[] {
  if (!owners?.length || !spaces?.length) return [];

  return owners
    .filter((owner) => owner.ownerType === 'Space' && !!owner.ownerId)
    .map((owner) => spaces.find((space) => space.id === owner.ownerId)?.type)
    .filter((type): type is string => !!type);
}

const STATUS_BADGES: Partial<
  Record<
    NonNullable<ImprovementsProgressItemModel['status']>,
    { label: string; color: LabelColorType }
  >
> = {
  Closed: {
    label: POST_INSPECTION_FLOW_STRINGS['progress.status.completed'],
    color: LABEL_COLOR_TYPES.GREEN,
  },
  InProgress: {
    label: POST_INSPECTION_FLOW_STRINGS['progress.status.in_progress'],
    color: LABEL_COLOR_TYPES.BLUE,
  },
};

// 'New' is absent on purpose: the payload cannot say whether it is awaiting
// approval or approved and not yet started, so any label would overclaim.
export function getStatusBadge(
  status: ImprovementsProgressItemModel['status'],
) {
  return status ? STATUS_BADGES[status] : undefined;
}

// Only these two render as photo evidence today - see ShadowMediaCard.js,
// which is the only other place in this codebase that branches on
// MaintenanceMediaModel['mediaType']. Pdf/Csv/Html are dropped rather than
// shown as broken tiles; nothing in the improvement-media flow currently
// produces them.
const RENDERABLE_MEDIA_TYPES: MaintenanceMediaModel['mediaType'][] = [
  'Image',
  'Video',
];

/**
 * Visibility is *not* this client's call for this payload: the server already
 * applies the rule when it builds ImprovementsProgressModel, dropping only
 * `Internal` and deliberately keeping untagged media, `Outcome` sub-owner
 * media and anything explicitly marked `Consumer`. ImprovementCard.js and
 * ImprovementModal.js read a different endpoint that does no such
 * pre-filtering, so their stricter rule is right for them and wrong here.
 * What is genuinely the client's job is renderability.
 */
export function getDisplayableMedia(
  media: ImprovementsProgressItemModel['media'],
): MaintenanceMediaModel[] {
  return (
    media?.filter(
      (item) =>
        !!item.url &&
        RENDERABLE_MEDIA_TYPES.includes(item.mediaType) &&
        item.subOwnerVisibility !== 'Internal',
    ) ?? []
  );
}

export function groupMediaByStage(media: MaintenanceMediaModel[]) {
  return {
    before: media.filter((m) => m.stage === 'Before'),
    during: media.filter((m) => m.stage === 'During'),
    after: media.filter((m) => m.stage === 'After'),
  };
}
